import React, { ReactNode } from "react";

import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tabs } from "antd";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { Button, ButtonProps, EntityTypeShapedIcon } from "@mapmycustomers/ui";

import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./EntityIconTabs.module.scss";

interface Props<T extends EntityType> {
  activeEntityType: T;
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  editable?: boolean;
  editProps?: Partial<ButtonProps & { tooltip: ReactNode }>;
  getDisabledText?: (type: T) => string;
  hideTabs?: boolean;
  isEntityTypeDisabled?: (type: T) => boolean;
  onEdit?: () => void;
  onSelect: (type: T) => void;
  title?: string;
}

const EntityIconTabs = <T extends EntityType>({
  activeEntityType,
  children,
  className,
  containerClassName,
  editable,
  editProps,
  getDisabledText,
  hideTabs,
  isEntityTypeDisabled,
  onEdit,
  onSelect,
  title,
}: Props<T>) => {
  const intl = useIntl();

  const { tooltip, ...otherEditButtonProps } = editProps ?? {};

  return (
    <div
      className={cn(
        styles.container,
        styles[`activeEntityType__${activeEntityType}`],
        containerClassName
      )}
    >
      {!hideTabs && (
        <Tabs
          activeKey={activeEntityType}
          className={styles.tabs}
          onChange={(key) => onSelect(key as T)}
          type="card"
        >
          {[EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL].map((tabEntityType) => {
            const disabled = isEntityTypeDisabled?.(tabEntityType as T);
            const entityName = getEntityTypeDisplayName(intl, tabEntityType, {
              lowercase: false,
              plural: true,
            });
            return (
              <Tabs.TabPane
                disabled={disabled}
                key={tabEntityType}
                tab={
                  <Tooltip
                    title={
                      disabled && getDisabledText ? getDisabledText(tabEntityType as T) : entityName
                    }
                  >
                    <div className={styles.entityTypeWrapper}>
                      <EntityTypeShapedIcon
                        className={cn(styles.entityTypeContainer, {
                          [styles.entityTypeContainerDisabled]: disabled,
                        })}
                        entityType={tabEntityType}
                        onClick={disabled ? undefined : () => onSelect(tabEntityType as T)}
                        size="xs"
                      />
                    </div>
                  </Tooltip>
                }
              />
            );
          })}
        </Tabs>
      )}
      <div className={cn(styles.content, className)} key={activeEntityType}>
        {title && (
          <div className={styles.title}>
            <div className={styles.name}>{title}</div>
            {editable && (
              <Tooltip title={tooltip}>
                <Button
                  {...otherEditButtonProps}
                  className={styles.actionIcon}
                  icon={<FontAwesomeIcon icon={faPen} />}
                  iconOnly
                  onClick={onEdit}
                  type="text"
                />
              </Tooltip>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default EntityIconTabs;
